import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { PMNotice } from "../../../utils/notifications";
import { PMButton } from "../../../utils/PMButton";
import { useUserHasAccess } from "../../../../routes/roleOnly";
import { analyzeReport, createOrderFromAnalysis } from "../../../../api/service/report";
import {
  hasSeriousAnalysisErrors,
  reportNeedsProcessPrefill,
} from "./report-process-defaults";

const QA_STATUS_STYLES = {
  pass: {
    icon: CheckCircleIcon,
    badge: "bg-green-100 text-green-800 border-green-200",
    iconColor: "text-green-600",
    border: "border-green-100 bg-green-50/40",
    fixText: "text-green-800",
  },
  warn: {
    icon: ExclamationTriangleIcon,
    badge: "bg-amber-100 text-amber-900 border-amber-200",
    iconColor: "text-amber-600",
    border: "border-amber-100 bg-amber-50/40",
    fixText: "text-amber-900",
  },
  fail: {
    icon: XCircleIcon,
    badge: "bg-red-100 text-red-800 border-red-200",
    iconColor: "text-red-600",
    border: "border-red-100 bg-red-50/40",
    fixText: "text-red-800",
  },
  none: {
    icon: SparklesIcon,
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    iconColor: "text-slate-500",
    border: "border-slate-100 bg-slate-50/80",
    fixText: "text-slate-700",
  },
};

const STATUS_SORT_ORDER = { fail: 0, warn: 1, pass: 2, none: 3 };

function normalizeMessage(message) {
  return (message || "").trim().toLowerCase();
}

function messagesMatch(a, b) {
  const left = normalizeMessage(a);
  const right = normalizeMessage(b);
  return left && right && left === right;
}

/** Prefer API check message; fall back to improvement text before UI fallbacks. */
function resolveCheckMessage(check, improvement) {
  return (
    coerceDisplayText(check?.message) ||
    coerceDisplayText(improvement?.message) ||
    ""
  );
}

function coerceDisplayText(value) {
  if (value == null || value === "") {
    return "";
  }
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
}

/**
 * Turn API `details` hashes into readable supplement text — never raw JSON.
 */
export function formatCheckDetails(checkKey, details, t) {
  if (!details) {
    return null;
  }
  if (typeof details === "string") {
    const trimmed = details.trim();
    return trimmed || null;
  }
  if (typeof details !== "object" || Array.isArray(details)) {
    return null;
  }

  switch (checkKey) {
    case "checklist_complete":
      if (
        typeof details.incomplete_count === "number" &&
        typeof details.total_count === "number"
      ) {
        return t("service.report.analyzer.details.checklist_complete_incomplete", {
          incomplete: details.incomplete_count,
          total: details.total_count,
        });
      }
      break;
    case "checklist_images":
      if (Array.isArray(details.items) && details.items.length > 0) {
        const titles = details.items
          .map((item) => (item && typeof item === "object" ? item.title : null))
          .filter(Boolean)
          .join(", ");
        if (titles) {
          return t("service.report.analyzer.details.checklist_images_missing", {
            titles,
          });
        }
      }
      break;
    case "deviations_noted":
      if (typeof details.count === "number") {
        return t("service.report.analyzer.details.deviations_count", {
          count: details.count,
        });
      }
      break;
    case "item_consumption":
      if (typeof details.count === "number") {
        return t("service.report.analyzer.details.item_consumption_unhandled", {
          count: details.count,
        });
      }
      break;
    case "variables_complete":
      if (typeof details.count === "number") {
        return t("service.report.analyzer.details.variables_complete_incomplete", {
          count: details.count,
        });
      }
      break;
    default:
      break;
  }

  return null;
}

/**
 * Merge API checks and improvements into one list keyed by `key`.
 * Improvement text is shown inline as fix guidance only when it adds information.
 */
export function mergeAnalysisDisplayItems(checks = [], improvements = []) {
  const improvementByKey = new Map();
  improvements.forEach((item) => {
    if (item?.key) {
      improvementByKey.set(item.key, item);
    }
  });

  const seenKeys = new Set();
  const items = [];

  checks.forEach((check) => {
    if (!check?.key) {
      return;
    }

    seenKeys.add(check.key);
    const improvement = improvementByKey.get(check.key);
    const fixMessage = resolveFixMessage(check, improvement);

    items.push({
      key: check.key,
      status: check.status || "none",
      message: resolveCheckMessage(check, improvement),
      fixMessage: coerceDisplayText(fixMessage) || null,
      details: check.details,
    });
  });

  improvements.forEach((improvement) => {
    if (!improvement?.key || seenKeys.has(improvement.key)) {
      return;
    }

    seenKeys.add(improvement.key);
    items.push({
      key: improvement.key,
      status: improvement.severity === "error" ? "fail" : "warn",
      message: coerceDisplayText(improvement.message),
      fixMessage: null,
      details: null,
    });
  });

  return items.sort(
    (a, b) =>
      (STATUS_SORT_ORDER[a.status] ?? 3) - (STATUS_SORT_ORDER[b.status] ?? 3)
  );
}

function resolveFixMessage(check, improvement) {
  if (!improvement?.message || check.status === "pass") {
    return null;
  }

  const checkMessage = coerceDisplayText(check?.message);
  const improvementMessage = coerceDisplayText(improvement.message);

  if (!checkMessage || messagesMatch(checkMessage, improvementMessage)) {
    return null;
  }

  return improvement.message;
}

function StatusBadge({ status, t }) {
  const style = QA_STATUS_STYLES[status] || QA_STATUS_STYLES.none;
  const Icon = style.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${style.badge}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {t(`service.report.analyzer.status.${status}`, { defaultValue: status })}
    </span>
  );
}

function CheckListItem({ item, t }) {
  const style = QA_STATUS_STYLES[item.status] || QA_STATUS_STYLES.none;
  const Icon = style.icon;
  const title = t(`service.report.analyzer.check.${item.key}`, {
    defaultValue: item.key,
  });
  const apiMessage = coerceDisplayText(item.message);
  const formattedDetails = formatCheckDetails(item.key, item.details, t);
  const messageText =
    apiMessage ||
    t(`service.report.analyzer.message_fallback.${item.status}`, {
      defaultValue: "",
    });
  const supplementaryDetails =
    apiMessage &&
    formattedDetails &&
    !messagesMatch(apiMessage, formattedDetails)
      ? formattedDetails
      : null;

  return (
    <li
      className={`rounded-2xl border px-4 py-3 ${style.border}`}
    >
      <div className="flex items-start gap-3">
        <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${style.iconColor}`} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-slate-900">{title}</div>
          {messageText ? (
            <p className="mt-1 text-sm text-slate-700">{messageText}</p>
          ) : null}
          {item.fixMessage ? (
            <p className={`mt-1 text-sm font-medium ${style.fixText}`}>
              {t("service.report.analyzer.fix_required")}: {item.fixMessage}
            </p>
          ) : null}
          {supplementaryDetails ? (
            <p className="mt-1 text-xs text-slate-500">{supplementaryDetails}</p>
          ) : null}
        </div>
      </div>
    </li>
  );
}

export function resolveAnalysisQaStatus(report) {
  const analysisStatus = report?.analysis_status || "none";
  const overallStatus = report?.analysis_data?.overall_status;

  if (["pass", "warn", "fail"].includes(analysisStatus)) {
    return analysisStatus;
  }

  if (["pass", "warn", "fail"].includes(overallStatus)) {
    return overallStatus;
  }

  if (analysisStatus === "none") {
    return "none";
  }

  // Legacy analysis review statuses map to QA result when possible.
  if (analysisStatus === "approved") {
    return overallStatus || "pass";
  }
  if (analysisStatus === "not_approved") {
    return overallStatus || "fail";
  }
  if (analysisStatus === "pending_review") {
    return overallStatus || "warn";
  }

  return "none";
}

export function reportHasRunAnalysis(report) {
  const qaStatus = resolveAnalysisQaStatus(report);
  return qaStatus !== "none" || Boolean(report?.analysis_run_at);
}

/** Analyzer owns the report-processing UX while running or after a run. */
export function reportAnalyzerIsActive(report, isRunning = false) {
  if (!report?.finished) {
    return false;
  }

  return isRunning || reportHasRunAnalysis(report);
}

export const ReportAnalyzerSection = ({
  report,
  onRefresh,
  onRequestProcess,
  onRunningChange,
  className = "",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isRunning, setIsRunning] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);

  const canManageReports = useUserHasAccess({ role: "service", action: "manage" });
  const canApproveReports =
    useUserHasAccess({ role: "service", action: "approve" }) || canManageReports;

  if (!report?.finished) {
    return null;
  }

  const analysisData = report?.analysis_data || {};
  const checks = analysisData?.checks || [];
  const improvements = analysisData?.improvements || [];
  const displayItems = mergeAnalysisDisplayItems(checks, improvements);
  const suggestedInvoice = analysisData?.suggested_invoice || {};
  const qaStatus = resolveAnalysisQaStatus(report);
  const hasRunAnalysis = reportHasRunAnalysis(report);
  const statusStyle = QA_STATUS_STYLES[qaStatus] || QA_STATUS_STYLES.none;
  const StatusIcon = statusStyle.icon;
  const seriousErrors = hasSeriousAnalysisErrors(analysisData, improvements);
  const processActionNeeded =
    seriousErrors && reportNeedsProcessPrefill(report);

  const handleRunAnalysis = async () => {
    setIsRunning(true);
    onRunningChange?.(true);
    try {
      const response = await analyzeReport(report.id);
      if (response?.data?.data) {
        toast.success(t("service.report.analyzer.run_success"));
        onRefresh?.({
          ...report,
          ...response.data.data,
          id: report.id,
          updated_at: new Date().toISOString(),
        });
      } else {
        toast.error(t("service.report.analyzer.run_failed"));
      }
    } catch (error) {
      const msg =
        error?.response?.data?.errors?.join(", ") ||
        t("service.report.analyzer.run_failed");
      toast.error(msg);
    } finally {
      setIsRunning(false);
      onRunningChange?.(false);
    }
  };

  const handleCreateOrder = async () => {
    setIsCreatingOrder(true);
    try {
      const response = await createOrderFromAnalysis(report.id);
      const body = response?.data;
      const orderId =
        body?.order?.id ??
        (body?.data?.id && body.data.id !== report?.id ? body.data.id : null);
      const updatedReport =
        body?.data?.id === report?.id ? body.data : null;
      toast.success(t("service.report.analyzer.order_created"));
      if (orderId) {
        navigate(`/dashboard/orders/${orderId}`);
      }
      if (updatedReport) {
        onRefresh?.(updatedReport);
      } else {
        onRefresh?.();
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.errors?.join(", ") ||
          t("service.report.analyzer.order_failed")
      );
    } finally {
      setIsCreatingOrder(false);
    }
  };

  return (
    <div className={`rounded-3xl border border-slate-200 bg-white shadow-sm ${className}`}>
      <div className="border-b border-slate-100 px-6 py-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <div className={`rounded-xl p-2 ${statusStyle.badge}`}>
              <StatusIcon className="h-6 w-6" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-semibold text-slate-950">
                  {t("service.report.analyzer.title")}
                </h3>
                {hasRunAnalysis ? <StatusBadge status={qaStatus} t={t} /> : null}
              </div>
              {hasRunAnalysis && coerceDisplayText(analysisData?.summary) ? (
                <p className="mt-2 text-sm text-slate-700">
                  {coerceDisplayText(analysisData.summary)}
                </p>
              ) : !hasRunAnalysis ? (
                <p className="mt-1 text-sm text-slate-600">
                  {t("service.report.analyzer.status.none")}
                </p>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <PMButton
              type="button"
              onClick={handleRunAnalysis}
              disabled={isRunning}
              className="bg-slate-900 hover:bg-slate-800"
            >
              {isRunning
                ? t("service.report.analyzer.running")
                : t("service.report.analyzer.run")}
            </PMButton>
            {canApproveReports ? (
              <PMButton
                type="button"
                onClick={() => onRequestProcess?.()}
                className={
                  processActionNeeded
                    ? "border-red-300 bg-red-600 hover:bg-red-700"
                    : undefined
                }
              >
                {t("Behandle rapport")}
              </PMButton>
            ) : null}
            {report?.approved && hasRunAnalysis ? (
              <PMButton
                type="button"
                onClick={handleCreateOrder}
                disabled={isCreatingOrder || report?.invoiced}
              >
                {isCreatingOrder
                  ? t("service.report.analyzer.creating_order")
                  : t("service.report.analyzer.create_order")}
              </PMButton>
            ) : null}
          </div>
        </div>
      </div>

      {hasRunAnalysis ? (
        <div className="space-y-6 px-6 py-5">
          {displayItems.length > 0 ? (
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                {t("service.report.analyzer.checks_heading")}
              </h4>
              <ul className="mt-3 space-y-2">
                {displayItems.map((item) => (
                  <CheckListItem key={item.key} item={item} t={t} />
                ))}
              </ul>
            </div>
          ) : null}

          {coerceDisplayText(suggestedInvoice?.text) ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
              <h4 className="text-sm font-semibold text-slate-800">
                {t("service.report.analyzer.suggested_invoice")}
              </h4>
              {coerceDisplayText(suggestedInvoice.title) ? (
                <p className="mt-2 text-sm font-medium text-slate-900">
                  {coerceDisplayText(suggestedInvoice.title)}
                </p>
              ) : null}
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
                {coerceDisplayText(suggestedInvoice.text)}
              </p>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="px-6 py-5">
          <PMNotice
            severity="info"
            title={t("service.report.analyzer.not_run_title")}
            description={t("service.report.analyzer.not_run_body")}
          />
        </div>
      )}
    </div>
  );
};
